import { Capacitor } from '@capacitor/core';
import { fetchImageProxy, getDownloadUrl } from '@/api/generation';

export type SaveGenerationImageStage =
  | 'download_url_failed'
  | 'proxy_failed'
  | 'native_bridge_missing'
  | 'native_url_invalid'
  | 'native_url_http_failed'
  | 'native_url_data_empty'
  | 'native_url_download_timeout'
  | 'native_url_download_failed'
  | 'native_url_save_failed'
  | 'photo_permission_denied'
  | 'native_save_failed'
  | 'native_save_timeout';

export type SaveGenerationImageMethod = 'native_url' | 'native_base64' | 'web_blob';

export type SaveGenerationImageResult = Blob & {
  saveMethod?: SaveGenerationImageMethod;
  saveStage?: string;
  saveDurationMs?: number;
  saveBytes?: number;
  saveHttpStatus?: number;
  saveUrlType?: string;
};

export class SaveGenerationImageError extends Error {
  stage: SaveGenerationImageStage;
  cause?: unknown;

  constructor(stage: SaveGenerationImageStage, message: string, cause?: unknown) {
    super(message);
    this.name = 'SaveGenerationImageError';
    this.stage = stage;
    this.cause = cause;
  }
}

type SaveGenerationImageOptions = {
  genId: string;
  imageUrl?: string;
  quality?: 'sd' | 'hd';
};

type NativeImageSaveResult = {
  method?: string;
  stage?: string;
  duration_ms?: number;
  bytes?: number;
  http_status?: number;
};

const NATIVE_SAVE_TIMEOUT_MS = 20_000;

function isPhotoPermissionError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return /denied|permission|authorization|access/i.test(message);
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function withSaveMetadata(
  blob: Blob,
  metadata: Pick<
    SaveGenerationImageResult,
    'saveMethod' | 'saveStage' | 'saveDurationMs' | 'saveBytes' | 'saveHttpStatus' | 'saveUrlType'
  >,
): SaveGenerationImageResult {
  return Object.assign(blob, metadata);
}

function classifyNativeUrlSaveError(err: unknown): SaveGenerationImageStage {
  if (err instanceof SaveGenerationImageError) return err.stage;
  const message = err instanceof Error ? err.message : String(err ?? '');
  if (/missing image url|invalid url/i.test(message)) return 'native_url_invalid';
  if (/download_timeout|timed out|timeout|超时/i.test(message)) return 'native_url_download_timeout';
  if (/http \d+|http_failed/i.test(message)) return 'native_url_http_failed';
  if (/empty data|data_empty/i.test(message)) return 'native_url_data_empty';
  if (/download[_ ]failed/i.test(message)) return 'native_url_download_failed';
  if (isPhotoPermissionError(err)) return 'photo_permission_denied';
  if (/save[_ ]failed|photo_save_failed/i.test(message)) return 'native_save_failed';
  return 'native_url_save_failed';
}

function canFallbackAfterNativeUrlError(stage: SaveGenerationImageStage): boolean {
  return (
    stage === 'native_url_invalid'
    || stage === 'native_url_http_failed'
    || stage === 'native_url_data_empty'
  );
}

async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  stage: SaveGenerationImageStage,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new SaveGenerationImageError(stage, 'Native image save timed out'));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

async function fetchPreferredImageBlob(
  options: SaveGenerationImageOptions,
  preferredUrl?: string,
): Promise<Blob> {
  const failures: unknown[] = [];

  if (preferredUrl) {
    try {
      return await fetchImageProxy(preferredUrl);
    } catch (err) {
      failures.push(err);
      console.warn('save image preferred proxy failed', {
        stage: 'proxy_failed',
        genId: options.genId,
        error: err,
      });
    }
  }

  try {
    const url = preferredUrl ?? (await getDownloadUrl(options.genId, options.quality ?? 'sd')).url;
    return await fetchImageProxy(url);
  } catch (err) {
    failures.push(err);
    console.warn('save image download url path failed', {
      stage: 'download_url_failed',
      genId: options.genId,
      error: err,
    });
  }

  if (options.imageUrl) {
    try {
      return await fetchImageProxy(options.imageUrl);
    } catch (err) {
      failures.push(err);
      console.warn('save image fallback proxy failed', {
        stage: 'proxy_failed',
        genId: options.genId,
        error: err,
      });
    }
  }

  throw new SaveGenerationImageError(
    failures.length === 1 ? 'download_url_failed' : 'proxy_failed',
    'Unable to fetch image blob for saving',
    failures,
  );
}

export async function saveGenerationImageToAlbum(
  options: SaveGenerationImageOptions,
): Promise<SaveGenerationImageResult> {
  const startedAt = Date.now();
  let preferredUrl: string | undefined;
  let preferredUrlType: string | undefined;

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    if (!window.__NATIVE_BRIDGE__?.saveImageFromUrl && !window.__NATIVE_BRIDGE__?.saveImage) {
      throw new SaveGenerationImageError(
        'native_bridge_missing',
        'Native image save bridge is not available',
      );
    }

    try {
      const download = await getDownloadUrl(options.genId, options.quality ?? 'sd');
      preferredUrl = download.url;
      preferredUrlType = download.url_type;
    } catch (err) {
      console.warn('save image download url path failed', {
        stage: 'download_url_failed',
        genId: options.genId,
        error: err,
      });
    }

    if (preferredUrl && window.__NATIVE_BRIDGE__?.saveImageFromUrl) {
      try {
        const nativeResult = await window.__NATIVE_BRIDGE__.saveImageFromUrl(
          preferredUrl,
        ) as NativeImageSaveResult | undefined;
        return withSaveMetadata(new Blob(), {
          saveMethod: 'native_url',
          saveStage: nativeResult?.stage ?? 'native_url_saved',
          saveDurationMs: nativeResult?.duration_ms ?? Date.now() - startedAt,
          saveBytes: nativeResult?.bytes,
          saveHttpStatus: nativeResult?.http_status,
          saveUrlType: preferredUrlType,
        });
      } catch (err) {
        const stage = classifyNativeUrlSaveError(err);
        console.warn('native url image save failed', {
          stage,
          genId: options.genId,
          error: err,
        });
        if (!canFallbackAfterNativeUrlError(stage)) {
          throw new SaveGenerationImageError(stage, 'Native URL image save failed', err);
        }
      }
    }
  }

  const blob = await fetchPreferredImageBlob(options, preferredUrl);

  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    if (!window.__NATIVE_BRIDGE__?.saveImage) {
      throw new SaveGenerationImageError(
        'native_bridge_missing',
        'Native image save bridge is not available',
      );
    }
    try {
      await withTimeout(
        window.__NATIVE_BRIDGE__.saveImage(await blobToBase64(blob)),
        NATIVE_SAVE_TIMEOUT_MS,
        'native_save_timeout',
      );
      return withSaveMetadata(blob, {
        saveMethod: 'native_base64',
        saveStage: 'native_base64_saved',
        saveDurationMs: Date.now() - startedAt,
        saveUrlType: preferredUrlType,
      });
    } catch (err) {
      const stage = err instanceof SaveGenerationImageError ? err.stage : isPhotoPermissionError(err)
        ? 'photo_permission_denied'
        : 'native_save_failed';
      console.warn('native image save failed', { stage, genId: options.genId, error: err });
      throw new SaveGenerationImageError(stage, 'Native image save failed', err);
    }
  }

  return withSaveMetadata(blob, {
    saveMethod: 'web_blob',
    saveStage: 'web_blob_ready',
    saveDurationMs: Date.now() - startedAt,
    saveUrlType: preferredUrlType,
  });
}
